import { noop } from '@capsicum/common';
import { InteractionLayer, ModalService, RadioButtonInput, type RadioButtonInputProps } from '@capsicum/common-web';
import { type SurveyAnswerProps } from '@capsicum/features/survey/shared';
import styled, { css, useTheme } from 'styled-components';

import { getModalIcon } from '../../util';
import { SurveyAnswerModal } from '../survey-answer-modal/SurveyAnswerModal';

export const SurveyAnswer = ({
	hasModalFor,
	id,
	mediaItems,
	onAnswerPress,
	onImageContainerPress = noop,
	showRadioButton,
	status = 'unselected',
	testID,
	text,
	thumbnailUrl = undefined
}: SurveyAnswerProps) => {
	const { colors } = useTheme();

	const imageContainerProps = {
		onClick: (e: React.MouseEvent) => {
			e.stopPropagation();

			onImageContainerPress();

			mediaItems?.length &&
				hasModalFor &&
				ModalService.addModal({
					renderComponent: () => <SurveyAnswerModal mediaItems={mediaItems} mediaType={hasModalFor} />
				});
		}
	};

	const radioButtonProps: RadioButtonInputProps = {
		checked: status === 'selected',
		disabled: status === 'disabled',
		onPress: () => showRadioButton && onAnswerPress(id)
	};

	const ModalIcon = getModalIcon(hasModalFor);

	return (
		<S.SurveyAnswerContainer
			$showRadioButton={showRadioButton}
			$status={status}
			data-testid={testID}
			onClick={() => showRadioButton && onAnswerPress(id)}
		>
			{thumbnailUrl && (
				<S.ImageContainer testID={`${testID}-openImage-button`} {...imageContainerProps}>
					<>
						<S.Image src={thumbnailUrl} />
						{ModalIcon && (
							<S.IconHolder>
								<ModalIcon color={colors.backgrounds.main.inverse} />
							</S.IconHolder>
						)}
					</>
				</S.ImageContainer>
			)}

			<S.TextContainer>
				<S.TextContent $status={status}>{text}</S.TextContent>
			</S.TextContainer>

			{showRadioButton && (
				<S.RadioButtonContainer>
					<RadioButtonInput testID={`${testID}-selectAnswer`} {...radioButtonProps} />
				</S.RadioButtonContainer>
			)}
		</S.SurveyAnswerContainer>
	);
};

namespace S {
	export const SurveyAnswerContainer = styled.div<{
		$showRadioButton: SurveyAnswerProps['showRadioButton'];
		$status: SurveyAnswerProps['status'];
	}>`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		overflow: hidden;
		width: 100%;
		min-height: 120px;
		cursor: pointer;
		${({ theme }) => theme.border.radius.medium};
		background: ${({ theme }) => theme.colors.backgrounds.main.subtle};
		border-width: ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.subtle};

		${({ $showRadioButton }) =>
			$showRadioButton &&
			css`
				padding-right: ${({ theme }) => theme.spacings.small};
			`};

		${({ $status }) =>
			($status === 'voted' || $status === 'selected') &&
			css`
				border-color: ${({ theme }) => theme.colors.borders.brand.enabled};
			`};

		${({ $status }) =>
			$status === 'voted' &&
			css`
				background-color: ${({ theme }) => theme.colors.backgrounds.brand.selected};
				cursor: default;
			`}

		${({ $status }) =>
			$status === 'disabled' &&
			css`
				opacity: 0.4;
				cursor: default;
			`};

		${({ $status }) =>
			$status === 'vote_pending' &&
			css`
				border-color: ${({ theme }) => theme.colors.backgrounds.status.participated};
			`}
	`;

	export const TextContainer = styled.div`
		flex: 1;
		align-items: center;
		justify-content: center;
	`;

	export const TextContent = styled.span<{ $status: string }>`
		display: block;
		text-align: center;
		padding: ${({ theme }) => theme.spacings.small};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};

		${({ $status }) =>
			$status === 'voted' &&
			css`
				color: ${({ theme }) => theme.colors.text.brand.enabled};
				${({ theme }) => theme.fonts.text.text1};
			`};

		${({ $status }) =>
			$status === 'vote_pending' &&
			css`
				color: ${({ theme }) => theme.colors.text.main.enabled};
				${({ theme }) => theme.fonts.text.text1};
			`};
	`;

	export const Image = styled.img`
		width: ${({ theme }) => theme.display.large};
		height: ${({ theme }) => theme.display.large};
		position: absolute;
		object-fit: cover;
	`;

	export const ImageContainer = styled(InteractionLayer)`
		width: ${({ theme }) => theme.display.large};
		height: ${({ theme }) => theme.display.large};
		position: relative;
	`;

	export const IconHolder = styled.div`
		position: absolute;
		left: ${({ theme }) => theme.spacings.xxxlarge};
		top: ${({ theme }) => theme.spacings.xxxlarge};
		width: ${({ theme }) => theme.sizing.large};
		height: ${({ theme }) => theme.sizing.large};
		display: flex;
		align-items: center;
		justify-content: center;
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		border: ${({ theme }) => theme.border.width.medium} solid ${({ theme }) => theme.colors.borders.main.selected};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const RadioButtonContainer = styled.div`
		margin-right: ${({ theme }) => theme.spacings.small};
	`;
}
